import { isSameCampaignScheduleDate } from './campaignScheduleDates';
import { BRAND_MAP, MARKET_REGION_MAP } from './deriveCampaignSchedulePreview';
import type { BrandSchedule, CampaignScheduleParentRow, CampaignSchedulePreview } from './types';

export function buildCampaignScheduleParentRows(
  preview: CampaignSchedulePreview | undefined,
): Array<CampaignScheduleParentRow> {
  return preview?.rows ?? [];
}

// A row can list many brands/markets, so matching is exact per brand x region combination and can be partial.
export function getMatchingBrandSchedules(
  parentRow: CampaignScheduleParentRow,
  brandSchedules: Array<BrandSchedule>,
): Array<BrandSchedule> {
  const brands = new Set(parentRow.brands.map((brand) => BRAND_MAP[brand]).filter(Boolean));
  const regions = new Set(parentRow.markets.map((market) => MARKET_REGION_MAP[market]).filter(Boolean));

  return brandSchedules.filter(
    (schedule) =>
      schedule.type === parentRow.type &&
      brands.has(schedule.brand) &&
      regions.has(schedule.region) &&
      isSameCampaignScheduleDate(schedule.start, parentRow.start) &&
      isSameCampaignScheduleDate(schedule.end, parentRow.end),
  );
}

export function getMatchingBrandSchedulesByParentRow(
  parentRows: Array<CampaignScheduleParentRow>,
  brandSchedules: Array<BrandSchedule>,
): Map<string, Array<BrandSchedule>> {
  return new Map(parentRows.map((parentRow) => [parentRow.id, getMatchingBrandSchedules(parentRow, brandSchedules)]));
}

// availability/online_purchase have no Salesforce record of their own (see deriveCampaignSchedulePreview),
// so they'd always show up as false-positive orphans below — only these two types are worth reconciling.
const RECONCILABLE_TYPES = new Set(['list_visibility', 'lux_plus']);

// Named "reconcilable" (not "unmatched") because it deliberately excludes availability/online_purchase —
// see RECONCILABLE_TYPES above.
export function getUnreconciledBrandSchedules(
  matchingByParentRowId: Map<string, Array<BrandSchedule>>,
  brandSchedules: Array<BrandSchedule>,
): Array<BrandSchedule> {
  const matchedIds = new Set<number>();

  matchingByParentRowId.forEach((schedules) => {
    schedules.forEach((schedule) => matchedIds.add(schedule.id));
  });

  return brandSchedules.filter((schedule) => RECONCILABLE_TYPES.has(schedule.type) && !matchedIds.has(schedule.id));
}
